import { useRef, useState } from 'react'
import {
  Avatar, Box, Button, CircularProgress, IconButton, LinearProgress,
  Stack, Tooltip, Typography,
} from '@mui/material'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined'
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import type { VendorDocument } from '@cmms/api-client'
import { DocumentViewer } from './DocumentViewer'

const FILE_ICONS: Record<string, React.ElementType> = {
  'application/pdf': PictureAsPdfOutlinedIcon,
  'application/msword': DescriptionOutlinedIcon,
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': DescriptionOutlinedIcon,
  'image/jpeg': ImageOutlinedIcon,
  'image/png': ImageOutlinedIcon,
  'image/webp': ImageOutlinedIcon,
}

const FILE_COLORS: Record<string, string> = {
  'application/pdf': '#DC2626',
  'application/msword': '#3B82F6',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '#3B82F6',
  'image/jpeg': '#059669',
  'image/png': '#059669',
}

function formatSize(bytes: number | null | undefined) {
  // Guard against null / undefined / 0 so we don't render "null B" for
  // attachments where the size wasn't captured at upload time
  // (workflow-copied QR photos, legacy rows).
  if (bytes == null || bytes <= 0) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

interface Props {
  documents: VendorDocument[]
  maxFiles?: number
  uploading: boolean
  uploadProgress: number | null
  onUpload: (file: File) => void
  onDelete: (docId: string) => void
  onView: (doc: VendorDocument) => Promise<{ data: Blob; headers?: any }>
  canEdit: boolean
}

export function DocumentUploader({ documents, maxFiles = 5, uploading, uploadProgress, onUpload, onDelete, onView, canEdit }: Props) {
  const [viewDoc, setViewDoc] = useState<VendorDocument | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragOver, setDragOver] = useState(false)

  function handleFiles(files: FileList | null) {
    if (!files?.length) return
    const file = files[0]!
    if (file.size > 25 * 1024 * 1024) { alert('File must be under 25 MB'); return }
    onUpload(file)
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault()
    setDragOver(false)
    handleFiles(e.dataTransfer.files)
  }

  return (
    <Box>
      {/* Document list */}
      {documents.length > 0 && (
        <Stack spacing={1} sx={{ mb: 2 }}>
          {documents.map((doc) => {
            const Icon = FILE_ICONS[doc.content_type ?? ''] ?? InsertDriveFileOutlinedIcon
            const color = FILE_COLORS[doc.content_type ?? ''] ?? '#64748B'
            return (
              <Stack key={doc.id} direction="row" alignItems="center" spacing={1.5}
                sx={{ px: 2, py: 1.25, borderRadius: 1.5, border: '1px solid', borderColor: 'divider',
                  transition: 'border-color 120ms', '&:hover': { borderColor: 'text.disabled' },
                  cursor: 'pointer' }}
                onClick={() => setViewDoc(doc)}>
                {doc.content_type?.startsWith('image/') ? (
                  <Box sx={{ width: 44, height: 44, borderRadius: 1.5, overflow: 'hidden', flexShrink: 0, border: '1px solid', borderColor: 'divider' }}>
                    <Box component="img" src={doc.file_url} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </Box>
                ) : (
                  <Avatar variant="rounded" sx={{ width: 44, height: 44, bgcolor: `${color}12`, color }}>
                    <Icon sx={{ fontSize: 20 }} />
                  </Avatar>
                )}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 600, lineHeight: 1.3 }} noWrap>{doc.file_name}</Typography>
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{formatSize(doc.file_size)}</Typography>
                    {doc.uploaded_by_name && (
                      <>
                        <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>·</Typography>
                        <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{doc.uploaded_by_name}</Typography>
                      </>
                    )}
                  </Stack>
                </Box>
                {canEdit && (
                  <Tooltip title="Remove" arrow>
                    <IconButton size="small"
                      onClick={(e) => { e.stopPropagation(); onDelete(doc.id) }}
                      sx={{ color: 'text.disabled', '&:hover': { color: 'error.main' } }}>
                      <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            )
          })}
        </Stack>
      )}

      {/* Upload progress */}
      {uploading && uploadProgress !== null && (
        <Box sx={{ mb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.75 }}>
            <CircularProgress size={16} />
            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Uploading... {uploadProgress}%</Typography>
          </Stack>
          <LinearProgress variant="determinate" value={uploadProgress}
            sx={{ height: 3, borderRadius: 99 }} />
        </Box>
      )}

      {/* Upload zone */}
      {canEdit && documents.length < maxFiles && !uploading && (
        <Box
          onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => inputRef.current?.click()}
          sx={{
            py: 2.5, textAlign: 'center', borderRadius: 2,
            border: '2px dashed', borderColor: dragOver ? 'primary.main' : 'divider',
            bgcolor: dragOver ? (t) => `${t.palette.primary.main}06` : 'transparent',
            cursor: 'pointer', transition: 'all 150ms',
            '&:hover': { borderColor: 'text.disabled', bgcolor: 'action.hover' },
          }}
        >
          <UploadFileOutlinedIcon sx={{ fontSize: 22, color: 'text.disabled', mb: 0.5 }} />
          <Typography sx={{ fontSize: 12.5, color: 'text.secondary', fontWeight: 500 }}>
            Drop files here or click to upload
          </Typography>
          <Typography sx={{ fontSize: 11, color: 'text.disabled', mt: 0.25 }}>
            PDF, Word, Excel, Images · Max 25 MB · {documents.length}/{maxFiles} files
          </Typography>
          <input ref={inputRef} type="file" style={{ display: 'none' }}
            accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.jpg,.jpeg,.png,.webp,.gif"
            onChange={(e) => { handleFiles(e.target.files); e.target.value = '' }} />
        </Box>
      )}

      {documents.length >= maxFiles && !uploading && (
        <Typography sx={{ fontSize: 11.5, color: 'text.disabled', textAlign: 'center', py: 1 }}>
          Maximum {maxFiles} documents reached
        </Typography>
      )}

      <DocumentViewer
        open={!!viewDoc}
        doc={viewDoc}
        directUrl={viewDoc?.file_url ?? null}
        fetchBlob={() => onView(viewDoc!)}
        onClose={() => setViewDoc(null)}
      />
    </Box>
  )
}
